import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Tag } from 'antd';
import {
  AlertOutlined,
  ApartmentOutlined,
  ApiOutlined,
  BarChartOutlined,
  BlockOutlined,
  CloudUploadOutlined,
  ClusterOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  ImportOutlined,
  InboxOutlined,
  LayoutOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/auth/OlAuthContext';
import type { OlAuthUser } from '@/auth/OlAuthContext';

interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: React.ReactNode;
  wave?: string;
  /**
   * Catalogue roles that can READ this surface (the permission-matrix read keys, per
   * docs/architecture/iam-rbac-uam-implementation.md §6). Hide-only: the nav is a courtesy, the API
   * is the real gate (403). Absent = ungated (always shown). See `canSee` for the fail-open stance.
   */
  readRoles?: string[];
}

// Role sets per surface, from the IAM matrix read keys. Kept data-driven (one map, not scattered
// conditionals) so the gate stays legible and easy to keep in step with the matrix.
const ITEMS: NavItem[] = [
  { key: 'workbench', path: '/workbench', label: 'Workbench', icon: <InboxOutlined />, readRoles: ['hub_admin', 'analyst', 'auditor'] },
  { key: 'flows', path: '/flows', label: 'Flows', icon: <ApartmentOutlined />, readRoles: ['hub_admin', 'flow_designer', 'flow_approver', 'analyst', 'auditor', 'hub_operator'] },
  { key: 'entities', path: '/entities', label: 'Entities', icon: <DeploymentUnitOutlined /> },
  { key: 'templates', path: '/templates', label: 'Templates', icon: <BlockOutlined />, readRoles: ['hub_admin', 'flow_designer', 'auditor'] },
  { key: 'migration', path: '/migration', label: 'Migration', icon: <ImportOutlined />, readRoles: ['hub_admin', 'flow_designer', 'auditor'] },
  { key: 'runs', path: '/runs', label: 'Runs', icon: <ThunderboltOutlined />, wave: 'PS-3' },
  { key: 'bulk', path: '/bulk', label: 'Bulk', icon: <CloudUploadOutlined />, readRoles: ['hub_admin', 'analyst', 'flow_designer', 'auditor'] },
  { key: 'simulations', path: '/simulations', label: 'Simulations', icon: <ExperimentOutlined />, wave: 'PS-2' },
  { key: 'screens', path: '/screens', label: 'Screen Studio', icon: <LayoutOutlined />, readRoles: ['hub_admin', 'hub_operator', 'flow_designer', 'auditor'] },
  { key: 'analytics', path: '/analytics', label: 'Analytics', icon: <BarChartOutlined />, readRoles: ['hub_admin', 'hub_operator', 'analyst', 'auditor'] },
  { key: 'alerts', path: '/alerts', label: 'Alerts', icon: <AlertOutlined />, readRoles: ['hub_admin', 'hub_operator', 'analyst', 'auditor'] },
  { key: 'oversight', path: '/oversight', label: 'Oversight', icon: <ClusterOutlined />, readRoles: ['hub_admin', 'hub_operator', 'auditor'] },
  { key: 'integrations', path: '/integrations', label: 'Integrations', icon: <ApiOutlined /> },
  { key: 'admin', path: '/admin', label: 'Admin', icon: <SettingOutlined />, readRoles: ['hub_admin'] },
];

/**
 * Visual gate for a nav surface. In the dev-standalone mock (VITE_AUTH_DISABLED) show everything so
 * every surface is reachable. Under real auth, gate on the surface's read-roles — but when roles are
 * UNKNOWN (Keycloak's default puts realm_access in the ACCESS token; a deployment may not expose it
 * to any readable source), the gate fails OPEN: hiding the nav from a legitimate user locks them out
 * of their own console, while showing it to an unauthorised user only reveals menu labels — the API
 * is the real enforcer (403) regardless. Never a security control.
 */
function canSee(item: NavItem, mock: boolean, user: OlAuthUser | null): boolean {
  if (!item.readRoles) return true; // ungated surface
  if (mock) return true;
  if (!user || !user.rolesKnown) return true; // roles UNKNOWN → fail open (API enforces)
  return item.readRoles.some((r) => user.roles.includes(r));
}

export function SideNav({ collapsed }: { collapsed: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, mock } = useAuth();

  const items = useMemo(() => ITEMS.filter((i) => canSee(i, mock, user)), [mock, user]);

  const selectedKey = useMemo(() => {
    // A case is a run seen whole, so it sits under Runs.
    const path = location.pathname.startsWith('/cases') ? '/runs' : location.pathname;
    const match = items.find((i) => path.startsWith(i.path));
    return match?.key ?? 'flows';
  }, [location.pathname, items]);

  return (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      onClick={({ key }) => {
        const item = items.find((i) => i.key === key);
        if (item) navigate(item.path);
      }}
      style={{ borderInlineEnd: 'none', paddingTop: 8 }}
      items={items.map((i) => ({
        key: i.key,
        icon: i.icon,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
            {i.label}
            {!collapsed && i.wave && (
              <Tag
                bordered={false}
                style={{
                  marginInlineStart: 'auto',
                  fontSize: 10,
                  lineHeight: '16px',
                  padding: '0 5px',
                  opacity: 0.7,
                }}
              >
                {i.wave}
              </Tag>
            )}
          </span>
        ),
      }))}
    />
  );
}
